import axios from "axios";
import { FundParseFormValues } from "./fundParseDefaults";
import {
    FundParseInputFile,
    FundParseJobHistoryResult,
    FundParseOutputFile,
    FundParseOutputManifest,
    FundParseOutputManifestGroup,
    FundParseExposureChartsSummary,
    FundParsePositionMappingSummary
} from "./fundParseTypes";
import { EXPOSURE_CHARTS_FILENAME } from "./fundParseExposureChartUtils";

const THUMBNAIL_EXTENSIONS = [".xlsx", ".csv", ".txt"];
const PIMCO_PARSED_STDOUT_RE = /✅ Pimco TPT parsed and validated:\s(\S+)/;
export const POSITION_MAPPINGS_FILENAME = "position_mappings.json";
export const OUTPUT_MANIFEST_FILENAME = "output_manifest.json";
export const FUND_PARSE_UNKNOWN_CIC_WARNING =
    "Warning: rows with unknown or empty CIC are in sheet Unclassified_CIC of *_ExcludedPositions.xlsx.";
const FUND_PARSE_UNKNOWN_CIC_WARNING_RE =
    /Warning:\s*rows with unknown or empty CIC are in sheet Unclassified_CIC of \*_ExcludedPositions\.xlsx\.?/i;

export function boolToJobArg(value: boolean): string {
    return value ? "true" : "false";
}

export function buildFundParseJobArgs(formValues: FundParseFormValues, inputFiles: FundParseInputFile[]): Record<string, unknown> {
    return {
        database: formValues.database,
        date: formValues.date,
        client_id: formValues.client_id,
        position_client_ids: formValues.position_client_ids.join(","),
        create_report: boolToJobArg(formValues.create_report),
        save_report: boolToJobArg(formValues.save_report),
        read_report: boolToJobArg(formValues.read_report),
        create_report_xlsx: boolToJobArg(formValues.create_report_xlsx),
        create_esg_input_xlsx: boolToJobArg(formValues.create_esg_input_xlsx),
        create_lookthrough_csvs: boolToJobArg(formValues.create_lookthrough_csvs),
        use_job_workspace: "true",
        input_files: inputFiles.map(({ fileName, base64 }) => ({ fileName, base64 }))
    };
}

export function withClientIdInPositionClientIds(clientId: string, positionClientIds: string[]): string[] {
    if (!clientId) {
        return positionClientIds;
    }
    if (positionClientIds.includes(clientId)) {
        return positionClientIds;
    }
    return [...positionClientIds, clientId];
}

export function formValuesForRun(values: FundParseFormValues, routeClientId?: string): FundParseFormValues {
    const clientId = routeClientId || values.client_id;
    return {
        ...values,
        client_id: clientId,
        position_client_ids: withClientIdInPositionClientIds(clientId, values.position_client_ids)
    };
}

export function validateFundParseRun(formValues: FundParseFormValues, inputFiles: FundParseInputFile[]): string | null {
    if (!formValues.client_id) {
        return "Client ID is required.";
    }

    if (!formValues.position_client_ids.length) {
        return "At least one position client ID is required.";
    }

    if (formValues.create_report && formValues.read_report) {
        return "Cannot create and read report at the same time.";
    }

    if (!formValues.create_report && !formValues.read_report) {
        return "Either create report or read report must be enabled.";
    }

    if (formValues.create_report && inputFiles.length === 0) {
        return "Upload at least one .xlsx input file when create report is enabled.";
    }

    return null;
}

export function isThumbnailOutputFile(fileName: string): boolean {
    const lowerName = fileName.toLowerCase();
    return THUMBNAIL_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
}

export function guessMimeType(fileName: string): string {
    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith(".xlsx")) {
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    }
    if (lowerName.endsWith(".csv")) {
        return "text/csv";
    }
    if (lowerName.endsWith(".json")) {
        return "application/json";
    }
    if (lowerName.endsWith(".txt")) {
        return "text/plain";
    }
    return "application/octet-stream";
}

export function parseFundParseOutputFiles(output: unknown): FundParseOutputFile[] {
    if (!output) {
        return [];
    }

    if (Array.isArray(output)) {
        const directFiles = output.filter(isOutputFileShape);
        if (directFiles.length > 0) {
            return directFiles.map(normalizeOutputFile);
        }
    }

    if (typeof output === "object" && output !== null) {
        const record = output as Record<string, unknown>;
        const candidates = [record.output_files, record.files, record.outputFiles];
        for (const candidate of candidates) {
            if (Array.isArray(candidate)) {
                const files = candidate.filter(isOutputFileShape).map(normalizeOutputFile);
                if (files.length > 0) {
                    return files;
                }
            }
        }
    }

    return [];
}

export function parseFundParseOutputManifest(outputFiles: FundParseOutputFile[]): FundParseOutputManifest | null {
    const manifestFile = outputFiles.find((file) => file.fileName.endsWith(OUTPUT_MANIFEST_FILENAME));
    if (!manifestFile) {
        return null;
    }

    try {
        const binary = atob(manifestFile.base64);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const jsonText = new TextDecoder().decode(bytes);
        const parsed = JSON.parse(jsonText) as FundParseOutputManifest;
        if (!parsed || !Array.isArray(parsed.groups)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function isInternalOutputFile(fileName: string, manifest: FundParseOutputManifest | null): boolean {
    if (fileName.endsWith(POSITION_MAPPINGS_FILENAME) || fileName.endsWith(OUTPUT_MANIFEST_FILENAME)) {
        return true;
    }
    if (fileName.endsWith(EXPOSURE_CHARTS_FILENAME)) {
        return true;
    }
    if (!manifest?.internalFiles?.length) {
        return false;
    }
    return manifest.internalFiles.some((file) => file.fileName === fileName);
}

export function getDownloadableOutputFiles(
    outputFiles: FundParseOutputFile[],
    manifest: FundParseOutputManifest | null
): FundParseOutputFile[] {
    if (manifest) {
        const downloadableNames = new Set<string>();
        for (const group of manifest.groups) {
            for (const file of group.files) {
                if (file.downloadable) {
                    downloadableNames.add(file.fileName);
                }
            }
        }
        return outputFiles.filter((file) => downloadableNames.has(file.fileName));
    }

    return outputFiles.filter((file) => !isInternalOutputFile(file.fileName, manifest));
}

export function resolveManifestGroupFiles(
    group: FundParseOutputManifestGroup,
    outputFiles: FundParseOutputFile[]
): FundParseOutputFile[] {
    const filesByName = new Map(outputFiles.map((file) => [file.fileName, file]));
    return group.files
        .filter((file) => file.downloadable)
        .map((file) => filesByName.get(file.fileName))
        .filter((file): file is FundParseOutputFile => typeof file !== "undefined");
}

export function buildLegacyOutputGroups(outputFiles: FundParseOutputFile[]): FundParseOutputManifestGroup[] {
    const downloadableFiles = outputFiles.filter(
        (file) =>
            !file.fileName.endsWith(POSITION_MAPPINGS_FILENAME) &&
            !file.fileName.endsWith(OUTPUT_MANIFEST_FILENAME) &&
            !file.fileName.endsWith(EXPOSURE_CHARTS_FILENAME)
    );
    if (!downloadableFiles.length) {
        return [];
    }

    return [
        {
            id: "legacy_outputs",
            label: "Output files",
            defaultExpanded: true,
            files: downloadableFiles.map((file) => ({
                fileName: file.fileName,
                label: file.fileName,
                downloadable: true
            }))
        }
    ];
}

export function parsePositionMappingSummary(outputFiles: FundParseOutputFile[]): FundParsePositionMappingSummary | null {
    const mappingFile = outputFiles.find((file) => file.fileName.endsWith(POSITION_MAPPINGS_FILENAME));
    if (!mappingFile) {
        return null;
    }

    try {
        const binary = atob(mappingFile.base64);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const jsonText = new TextDecoder().decode(bytes);
        const parsed = JSON.parse(jsonText) as FundParsePositionMappingSummary;
        if (!parsed || !Array.isArray(parsed.rows)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function parseExposureChartsSummary(outputFiles: FundParseOutputFile[]): FundParseExposureChartsSummary | null {
    const chartsFile = outputFiles.find((file) => file.fileName.endsWith(EXPOSURE_CHARTS_FILENAME));
    if (!chartsFile) {
        return null;
    }

    try {
        const binary = atob(chartsFile.base64);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const jsonText = new TextDecoder().decode(bytes);
        const parsed = JSON.parse(jsonText) as FundParseExposureChartsSummary;
        if (!parsed || !parsed.dimensions) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function isOutputFileShape(value: unknown): value is FundParseOutputFile {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>;
    return typeof record.fileName === "string" && typeof record.base64 === "string";
}

function normalizeOutputFile(file: FundParseOutputFile): FundParseOutputFile {
    return {
        fileName: file.fileName,
        base64: file.base64,
        mimeType: file.mimeType || guessMimeType(file.fileName)
    };
}

export function readFileAsBase64(file: File): Promise<{ fileName: string; base64: string; size: number }> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => {
            reject(new Error(`Could not read file "${file.name}".`));
        };
        reader.onload = (event) => {
            const result = event.target?.result;
            if (typeof result !== "string") {
                reject(new Error(`Could not read file "${file.name}".`));
                return;
            }
            const commaIndex = result.indexOf(",");
            const base64 = commaIndex >= 0 ? result.slice(commaIndex + 1) : result;
            resolve({ fileName: file.name, base64, size: file.size });
        };
        reader.readAsDataURL(file);
    });
}

export function createInputFileId(fileName: string, size: number): string {
    return `${fileName}-${size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function downloadOutputFile(file: FundParseOutputFile): void {
    const mimeType = file.mimeType || guessMimeType(file.fileName);
    const blob = base64ToBlob(file.base64, mimeType);
    const link = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = file.fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
}

export function downloadAllOutputFiles(files: FundParseOutputFile[]): void {
    files.forEach((file, index) => {
        window.setTimeout(() => downloadOutputFile(file), index * 250);
    });
}

function base64ToBlob(base64: string, mimeType: string): Blob {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return new Blob([bytes], { type: mimeType });
}

export interface ParsedJobStdoutStderr {
    stdoutLines: string[];
    stderrLines: string[];
}

export function parsePimcoParsedInputFileNames(stdoutLines: string[]): string[] {
    const fileNames = new Set<string>();
    for (const line of stdoutLines) {
        const match = PIMCO_PARSED_STDOUT_RE.exec(line);
        if (match) {
            fileNames.add(match[1]);
        }
    }
    return Array.from(fileNames);
}

export function normalizeFundParseJobStatus(status: unknown): FundParseJobHistoryResult["status"] | undefined {
    if (status === 0 || status === "0" || status === "completed") {
        return "completed";
    }
    if (status === 1 || status === "1" || status === "failed") {
        return "failed";
    }
    if (status === "delayed" || status === "active" || status === "waiting" || status === "paused") {
        return status;
    }
    return undefined;
}

export function parseJobStdoutStderr(output: unknown): ParsedJobStdoutStderr {
    if (Array.isArray(output)) {
        return {
            stdoutLines: output.map((line) => String(line)),
            stderrLines: []
        };
    }

    if (!output || typeof output !== "object") {
        return { stdoutLines: [], stderrLines: [] };
    }

    const record = output as Record<string, unknown>;
    const stdout = record.stdOut || record.stdout;
    const stderr = record.stdError || record.stderr;

    return {
        stdoutLines: Array.isArray(stdout) ? stdout.map((line) => String(line)) : [],
        stderrLines: Array.isArray(stderr) ? stderr.map((line) => String(line)) : []
    };
}

export function isFundParseUnknownCicWarning(message: string): boolean {
    return FUND_PARSE_UNKNOWN_CIC_WARNING_RE.test(message.trim());
}

export function partitionFundParseJobMessages(messages: string[]): { errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    for (const message of messages) {
        const trimmed = message.trim();
        if (!trimmed) {
            continue;
        }
        if (isFundParseUnknownCicWarning(trimmed)) {
            warnings.push(trimmed);
            continue;
        }
        errors.push(message);
    }

    return { errors, warnings };
}

export function extractFundParseJobWarningMessages(history: FundParseJobHistoryResult): string[] {
    const { stdoutLines, stderrLines } = parseJobStdoutStderr(history.output);
    const warnings = new Set<string>();

    for (const line of [...stdoutLines, ...stderrLines]) {
        if (isFundParseUnknownCicWarning(line)) {
            warnings.add(line.trim());
        }
    }

    return Array.from(warnings);
}

export function isFundParseJobFailed(history: FundParseJobHistoryResult): boolean {
    const { stderrLines } = parseJobStdoutStderr(history.output);
    const { errors, warnings } = partitionFundParseJobMessages(stderrLines);
    const stacktraceLines = Array.isArray(history.stacktrace)
        ? history.stacktrace.map((line) => String(line)).filter((line) => line.trim())
        : [];

    if (errors.length > 0 || stacktraceLines.length > 0) {
        return true;
    }

    if (warnings.length > 0) {
        return false;
    }

    return history.status === "failed";
}

export function extractFundParseJobErrorMessages(history: FundParseJobHistoryResult): string[] {
    const messages: string[] = [];
    const { stderrLines } = parseJobStdoutStderr(history.output);

    messages.push(...partitionFundParseJobMessages(stderrLines).errors);

    if (Array.isArray(history.stacktrace)) {
        const stackLines = history.stacktrace.map((line) => String(line)).filter((line) => line.trim());
        if (stackLines.length > 0) {
            messages.push(...stackLines);
        }
    }

    if (messages.length === 0 && isFundParseJobFailed(history)) {
        messages.push("The job failed without a detailed error message.");
    }

    return messages;
}

export function formatFundParseJobFailureAlert(history: FundParseJobHistoryResult): string {
    const messages = extractFundParseJobErrorMessages(history);
    if (messages.length === 0) {
        return "Job finished with failure.";
    }
    return messages.join("\n");
}

export function formatAxiosFundParseError(error: unknown): string[] {
    if (axios.isAxiosError(error)) {
        const responseData = error.response?.data;
        if (responseData && typeof responseData === "object") {
            const record = responseData as Record<string, unknown>;
            if (typeof record.error === "string" && record.error.trim()) {
                return [record.error];
            }
            if (typeof record.message === "string" && record.message.trim()) {
                return [record.message];
            }
        }
        if (error.message) {
            return [error.message];
        }
    }
    if (error instanceof Error) {
        return [error.message];
    }
    return ["Failed to run job."];
}

export function createConsoleLineId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function formatJobStatusMessage(status: string | undefined): string {
    const timestamp = new Date().toLocaleTimeString();
    if (!status) {
        return `[${timestamp}] Waiting for job status...`;
    }
    return `[${timestamp}] Status: ${status}`;
}

export async function revealStdoutLines(
    lines: string[],
    onLine: (line: string) => void | Promise<void>,
    delayMs = 35
): Promise<void> {
    for (const line of lines) {
        await onLine(line);
        if (delayMs > 0) {
            await sleep(delayMs);
        }
    }
}

export function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

/** @deprecated Use parseJobStdoutStderr and the job console instead. */
export function formatJobLogOutput(output: unknown): string {
    const { stdoutLines, stderrLines } = parseJobStdoutStderr(output);
    const lines = [...stdoutLines];
    if (stderrLines.length > 0) {
        lines.push("", "Errors:", ...stderrLines);
    }
    if (lines.length > 0) {
        return lines.join("\n");
    }
    if (output && typeof output === "object") {
        return JSON.stringify(output, null, 2);
    }
    return output ? String(output) : "";
}
